import {
  AccountPayableLiteModel,
  PaymentLiteModel,
  useGetAccountPayables,
} from 'api-hooks/account-payable';
import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import AccountPayableTable from './components/table';
import TransactionPillStatus from '../component/status';
import TransactionListTabs from '../component/transaction-list-tab';

export default function AccountPayableList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<AccountPayableLiteModel>[]>(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 150,
        cell: ({ getValue }) => (
          <TransactionPillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        maxSize: 150,
        sortName: 'created_at',
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:number'),
        accessorKey: 'number',
        minSize: 100,
        sortName: 'number',
      },
      {
        header: t('common:agent'),
        accessorKey: 'agent.name',
        minSize: 200,
        sortName: 'agent',
      },
      {
        header: t('common:order_number'),
        accessorKey: 'payment',
        minSize: 200,
        cell: ({ getValue }) => {
          const { paymentItems } = getValue() as PaymentLiteModel;
          const orderNumber = paymentItems
            .map((item) => item.transactionable.number)
            .join(', ');
          return <>{orderNumber}</>;
        },
      },
      {
        header: t('debt:payment_amount'),
        accessorKey: 'payment.amount',
        minSize: 200,
        textAlign: 'right',
        sortName: 'payment_amount',
        cell: ({ getValue }) => <>{string2money(getValue() as string)}</>,
      },
      {
        header: t('debt:payment_type'),
        accessorKey: 'payment',
        minSize: 100,
        cell: ({ getValue }) => {
          const { paymentMethod } = getValue() as PaymentLiteModel;
          return <> {t(`common:${paymentMethod.type}`)}</>;
        },
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
        sortName: 'description',
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue(),
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetAccountPayables,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.AccountPayablesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('account-payables');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:account_payable')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.AccountPayablesCreate}
        />
        <TransactionListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <AccountPayableTable
          accountPayables={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
